import { Mark } from 'prosemirror-model';
import { logger } from '../../logger';
const openSuffix = '_open';
const closeSuffix = '_close';
var TokenType;
(function (TokenType) {
    TokenType["open"] = "open";
    TokenType["close"] = "close";
    TokenType["default"] = "default";
})(TokenType || (TokenType = {}));
export class MarkdownParser {
    constructor(schema, tokenizer, tokens) {
        this.stack = [];
        this.schema = schema;
        this.marks = Mark.none;
        this.tokens = tokens;
        this.tokenizer = tokenizer;
    }
    validateLink(url) {
        return this.tokenizer.validateLink(url);
    }
    normalizeLink(url) {
        return this.tokenizer.normalizeLink(url);
    }
    normalizeLinkText(url) {
        return this.tokenizer.normalizeLinkText(url);
    }
    matchLinks(text) {
        return this.tokenizer.linkify.match(text);
    }
    parse(text) {
        const time = Date.now();
        try {
            this.stack = [{ type: this.schema.topNodeType, content: [] }];
            let yfmTokens;
            try {
                yfmTokens = this.tokenizer.parse(text, {});
            }
            catch (err) {
                const e = err;
                e.message = 'Unable to parse your markup. Please check for errors. ' + e.message;
                throw e;
            }
            this.parseTokens(yfmTokens);
            let doc;
            // If after parsing there are still unclosed nodes, close them by removing them from the stack.
            do {
                doc = this.closeNode();
            } while (this.stack.length);
            return (doc || this.schema.topNodeType.createAndFill());
        }
        finally {
            logger.metrics({ component: 'parser', event: 'parse', duration: Date.now() - time });
        }
    }
    top() {
        return this.stack[this.stack.length - 1];
    }
    push(elt) {
        if (this.stack.length)
            this.top().content.push(elt);
    }
    //#region helpers
    getTokenAttrs(yfmToken, tokenSpec, tokenStream, i) {
        if (tokenSpec.getAttrs)
            return tokenSpec.getAttrs(yfmToken, tokenStream, i);
        else if (tokenSpec.attrs instanceof Function)
            return tokenSpec.attrs(yfmToken);
        return tokenSpec.attrs;
    }
    getTokenSpec(yfmToken) {
        var _a;
        // Matching the pm-node meta-attribute which is set in the plugin for markdown-it
        let tokName = ((_a = yfmToken.meta) === null || _a === void 0 ? void 0 : _a['pm-node']) || yfmToken.type;
        // Cropping _open and _close from node name end
        // e.g. paragraph_open -> paragraph
        tokName = tokName.replace(new RegExp(`(${openSuffix})$|(${closeSuffix})$`), '');
        let tokenSpec;
        if (tokName in this.tokens)
            tokenSpec = this.tokens[tokName];
        if (!tokenSpec)
            throw new RangeError(`No token spec for token: ${JSON.stringify(yfmToken)}`);
        return tokenSpec;
    }
    // Getting token type from its name
    // e.g. "paragraph_open" - is opening token for paragraph
    getTokenType({ type }) {
        if (type.endsWith(openSuffix))
            return TokenType.open;
        if (type.endsWith(closeSuffix))
            return TokenType.close;
        return TokenType.default;
    }
    //#endregion
    //#region token handlers
    // Self-explanatory
    handlePrimitiveToken(yfmToken) {
        switch (yfmToken.type) {
            case 'text':
                this.addText(withoutTrailingNewline(yfmToken.content));
                return true;
            case 'inline':
                this.parseTokens(yfmToken.children || []);
                return true;
            case 'softbreak':
                // TODO: move all primitive token's handlers to extensions
                if (this.tokens.softbreak)
                    return false;
                this.addText('\n');
                return true;
        }
        return false;
    }
    handleMark(yfmToken, tokenSpec, attrs) {
        const schemaSpec = this.schema.marks[tokenSpec.name];
        if (tokenSpec.noCloseToken) {
            this.openMark(schemaSpec.create(attrs));
            let { content } = yfmToken;
            if (!tokenSpec.code)
                content = withoutTrailingNewline(content);
            this.addText(content);
            this.closeMark(schemaSpec);
            return;
        }
        switch (this.getTokenType(yfmToken)) {
            case 'open': {
                this.openMark(schemaSpec.create(attrs));
                break;
            }
            case 'close': {
                this.closeMark(schemaSpec);
                break;
            }
        }
    }
    handleNode(_yfmToken, tokenSpec, attrs) {
        const schemaSpec = this.schema.nodes[tokenSpec.name];
        // Adding node as is, becasuse it doesn't contain content.
        this.addNode(schemaSpec, attrs);
    }
    handleBlock(yfmToken, tokenSpec, attrs) {
        var _a;
        const schemaSpec = this.schema.nodes[tokenSpec.name];
        if (tokenSpec.noCloseToken) {
            this.openNode(schemaSpec, attrs);
            if (tokenSpec.contentField === 'children' && ((_a = yfmToken.children) === null || _a === void 0 ? void 0 : _a.length)) {
                this.parseTokens(yfmToken.children);
            }
            else {
                let { content } = yfmToken;
                if (tokenSpec.prepareContent) {
                    content = tokenSpec.prepareContent(content);
                }
                this.addText(content);
            }
            this.closeNode();
            return;
        }
        switch (this.getTokenType(yfmToken)) {
            case 'open': {
                this.openNode(schemaSpec, attrs);
                break;
            }
            case 'close': {
                this.closeNode();
                break;
            }
        }
    }
    handleToken(yfmToken, tokenSpec, attrs) {
        switch (tokenSpec.type) {
            case 'mark':
                this.handleMark(yfmToken, tokenSpec, attrs);
                break;
            case 'node':
                this.handleNode(yfmToken, tokenSpec, attrs);
                break;
            case 'block':
                this.handleBlock(yfmToken, tokenSpec, attrs);
                break;
        }
    }
    //#endregion
    //#region state methods
    addNode(type, attrs, content) {
        const node = type.createAndFill(attrs, content, this.marks);
        if (!node)
            return null;
        this.push(node);
        return node;
    }
    openNode(type, attrs) {
        this.stack.push({ type: type, attrs, content: [] });
    }
    closeNode() {
        // Marks operate within a node. Therefore, when we close the node, we reset the existing marks.
        if (this.marks.length)
            this.marks = Mark.none;
        const info = this.stack.pop();
        if (info)
            return this.addNode(info.type, info.attrs, info.content);
        return null;
    }
    addText(text) {
        if (!text)
            return;
        const nodes = this.top().content;
        const last = nodes[nodes.length - 1];
        const node = this.schema.text(text, this.marks);
        let merged;
        if (last && (merged = maybeMerge(last, node)))
            nodes[nodes.length - 1] = merged;
        else
            nodes.push(node);
    }
    // Adding mark to a set of open marks
    openMark(mark) {
        this.marks = mark.addToSet(this.marks);
    }
    closeMark(mark) {
        this.marks = mark.removeFromSet(this.marks);
    }
    //#endregion
    parseTokens(yfmTokens) {
        for (let i = 0; i < yfmTokens.length; i++) {
            const yfmToken = yfmTokens[i];
            if (this.handlePrimitiveToken(yfmToken))
                continue;
            const tokenSpec = this.getTokenSpec(yfmToken);
            if (tokenSpec.ignore)
                continue;
            const attrs = this.getTokenAttrs(yfmToken, tokenSpec, yfmTokens, i);
            this.handleToken(yfmToken, tokenSpec, attrs);
        }
    }
}
// Checking if these are two text nodes and they have the same marks, then it merges them into one.
function maybeMerge(a, b) {
    if (a.isText && b.isText && Mark.sameSet(a.marks, b.marks)) {
        // Somehow this method is not in typings but it exists.
        // @ts-expect-error
        return a.withText(a.text + b.text);
    }
}
function withoutTrailingNewline(str) {
    return str[str.length - 1] === '\n' || str.endsWith('\\n') ? str.slice(0, str.length - 1) : str;
}
